'use client'

import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react'
import { BlurFade } from '@/components/ui/blur-fade'
import { api } from '@/convex/_generated/api'
import { useAuthedPaginatedQuery } from '@/hooks/use-authed-query'
import { usePageContentReadiness } from '@/hooks/use-page-content-readiness'
import { TEMPLATE_GRID_CLASSES } from '../lib/grid-classes'
import type { TemplateSnapshot } from '../lib/types'
import { buildTemplateEmailPath } from '../lib/url-helpers'
import { EmailPreviewCard } from './template-card'
import { ForwardedEmptyState } from './templates-forwarded-empty-state'

interface ForwardedEmailGridProps {
  initialEmails: Array<TemplateSnapshot>
  userId: string
}

const PAGE_SIZE = 30
const subscribeToHydration = () => () => {}
const getHydratedClientSnapshot = () => true
const getHydratedServerSnapshot = () => false

type ForwardedTemplateRow = Readonly<{
  emailId: string
  title: string
  category?: string
  imagePngUrl?: string
  emailHtml?: string
  brandDomain?: string
  status?: TemplateSnapshot['status']
  remixCount?: number
}>

function toSnapshot(row: ForwardedTemplateRow): TemplateSnapshot {
  return {
    emailId: row.emailId,
    title: row.title,
    category: row.category,
    imagePngUrl: row.imagePngUrl,
    emailHtml: row.emailHtml,
    brandDomain: row.brandDomain,
    status: row.status,
    remixCount: row.remixCount,
  }
}

export function ForwardedEmailGrid({
  initialEmails,
  userId,
}: ForwardedEmailGridProps) {
  const sentinelRef = useRef<HTMLDivElement>(null)
  const { results, status, loadMore } = useAuthedPaginatedQuery(
    api.templates.user_queries.getUserTemplatesPage,
    { userId },
    { initialNumItems: PAGE_SIZE }
  )
  const isHydrated = useSyncExternalStore(
    subscribeToHydration,
    getHydratedClientSnapshot,
    getHydratedServerSnapshot
  )

  const emails = useMemo(() => {
    const isLivePageReady = isHydrated && status !== 'LoadingFirstPage'
    const source = isLivePageReady ? results : initialEmails
    const seen = new Set<string>()
    return source.map(toSnapshot).filter(({ emailId }) => {
      if (seen.has(emailId)) {
        return false
      }
      seen.add(emailId)
      return true
    })
  }, [initialEmails, isHydrated, results, status])

  usePageContentReadiness(
    ['web_templates', 'web_browse_templates'],
    emails.length === 0 ? 'empty' : 'ready'
  )

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!(sentinel && status === 'CanLoadMore')) {
      return
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          loadMore(PAGE_SIZE)
        }
      },
      { rootMargin: '400px' }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [loadMore, status])

  if (emails.length === 0) {
    return <ForwardedEmptyState />
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className={TEMPLATE_GRID_CLASSES}>
        {emails.map((snapshot, index) => (
          <BlurFade key={snapshot.emailId} inView delay={(index % 6) * 0.06}>
            <EmailPreviewCard
              snapshot={snapshot}
              href={buildTemplateEmailPath(snapshot.emailId)}
            />
          </BlurFade>
        ))}
      </div>
      {status === 'CanLoadMore' || status === 'LoadingMore' ? (
        <div ref={sentinelRef} className="h-px w-full" />
      ) : null}
    </div>
  )
}
